// src/DynamicDmmChart.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart, LineSeries, HistogramSeries } from "lightweight-charts";

/**
 * DynamicDmmChart
 * - 3 curvas (dmmRel, vRel, aRel) na MESMA escala -> zero alinhado
 * - histograma inferior:
 *   - Top / Pré-topo (verde) e Vale / Pré-vale (vermelho)
 *   - ✅ SEM NEGATIVOS: tudo positivo; estado é indicado por cor/alpha
 *   - ✅ Continuidade: pré = translúcido, confirmado = sólido; altura segue o streak do estado atual
 * - tooltip com valores das 3 curvas + estado (top/preTop/valley/preValley)
 * - sync de timeRange com o pai
 */
export default function DynamicDmmChart({ data, syncedTimeRange, onTimeRangeChange }) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);

  const chartRef = useRef(null);
  const isSyncingRef = useRef(false);

  const dmmRelSeriesRef = useRef(null);
  const vRelSeriesRef = useRef(null);
  const aRelSeriesRef = useRef(null);
  const topSeriesRef = useRef(null);

  const dataRef = useRef([]);
  const [hasData, setHasData] = useState(true);
  const [hover, setHover] = useState(null);

  const formatTime = (t) => {
    if (!t) return "";
    if (typeof t === "string") return t;
    const y = t.year;
    const m = String(t.month).padStart(2, "0");
    const d = String(t.day).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const fmt = (x, decimals = 6) => {
    if (x == null || !Number.isFinite(Number(x))) return "—";
    return Number(x).toFixed(decimals);
  };

  // ============== create chart (once) ==============
  useEffect(() => {
    const container = containerRef.current;
    if (!container || chartRef.current) return;

    const width =
      container.getBoundingClientRect().width > 0
        ? container.getBoundingClientRect().width
        : 600;

    const chart = createChart(container, {
      width,
      height: 340,
      layout: {
        background: { type: "solid", color: "rgba(10,10,12,0.0)" },
        textColor: "rgba(255,255,255,0.78)",
      },
      crosshair: { mode: 1 },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.06)" },
        horzLines: { color: "rgba(255,255,255,0.06)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.12)" },
      timeScale: { borderColor: "rgba(255,255,255,0.12)" },
    });

    // ✅ 3 curvas na MESMA escala ("right") => zero alinhado
    const dmm = chart.addSeries(LineSeries, {
      color: "#42a5f5",
      lineWidth: 2,
      priceScaleId: "right",
      lastValueVisible: true,
      priceLineVisible: true,
    });

    const v = chart.addSeries(LineSeries, {
      color: "#ffeb3b",
      lineWidth: 2,
      priceScaleId: "right",
      lastValueVisible: true,
      priceLineVisible: true,
    });

    const a = chart.addSeries(LineSeries, {
      color: "#ef6c00",
      lineWidth: 2,
      priceScaleId: "right",
      lastValueVisible: true,
      priceLineVisible: true,
    });

    // ✅ histograma em uma faixa inferior (Top/PreTop/Valley/PreValley)
    const top = chart.addSeries(HistogramSeries, {
      priceScaleId: "top",
      scaleMargins: { top: 0.86, bottom: 0.02 },
      lastValueVisible: false,
      priceLineVisible: false,
    });

    chart.priceScale("top").applyOptions({
      scaleMargins: { top: 0.86, bottom: 0.02 },
      borderColor: "rgba(255,255,255,0.12)",
      textColor: "rgba(255,255,255,0.45)",
    });

    chartRef.current = chart;
    dmmRelSeriesRef.current = dmm;
    vRelSeriesRef.current = v;
    aRelSeriesRef.current = a;
    topSeriesRef.current = top;

    // resize
    const handleResize = () => {
      if (!chartRef.current || !containerRef.current) return;
      const w = containerRef.current.getBoundingClientRect().width;
      if (w > 0) chartRef.current.applyOptions({ width: w });
    };
    window.addEventListener("resize", handleResize);

    // crosshair tooltip
    const handleCrosshairMove = (param) => {
      try {
        if (
          !param.time ||
          !param.point ||
          !wrapperRef.current ||
          param.point.x < 0 ||
          param.point.y < 0 ||
          param.point.x > wrapperRef.current.clientWidth ||
          param.point.y > wrapperRef.current.clientHeight
        ) {
          setHover(null);
          return;
        }

        if (!param.seriesPrices || typeof param.seriesPrices.get !== "function") {
          setHover(null);
          return;
        }

        const sDmm = param.seriesPrices.get(dmmRelSeriesRef.current);
        const sV = param.seriesPrices.get(vRelSeriesRef.current);
        const sA = param.seriesPrices.get(aRelSeriesRef.current);
        const sTop = param.seriesPrices.get(topSeriesRef.current);

        if (sDmm == null && sV == null && sA == null && sTop == null) {
          setHover(null);
          return;
        }

        const dateStr = formatTime(param.time);
        const row = dataRef.current.find((r) => r.date === dateStr);

        setHover({
          date: dateStr,
          dmmRel: typeof sDmm === "number" ? sDmm : null,
          vRel: typeof sV === "number" ? sV : null,
          aRel: typeof sA === "number" ? sA : null,
          topHist: typeof sTop === "number" ? sTop : null,

          rating: row?.rating ?? "—",

          topStreak: Number(row?.topStreak ?? 0),
          isTop: row?.isTop === true,

          preTopStreak: Number(row?.preTopStreak ?? 0),
          isPreTop: row?.isPreTop === true,

          valleyStreak: Number(row?.valleyStreak ?? 0),
          isValley: row?.isValley === true,

          preValleyStreak: Number(row?.preValleyStreak ?? 0),
          isPreValley: row?.isPreValley === true,
        });
      } catch {
        setHover(null);
      }
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    // sync range -> pai
    const handleRangeChange = (range) => {
      if (!onTimeRangeChange || isSyncingRef.current) return;
      if (!range) return;
      onTimeRangeChange(range);
    };
    chart.timeScale().subscribeVisibleTimeRangeChange(handleRangeChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.timeScale().unsubscribeVisibleTimeRangeChange(handleRangeChange);

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      dmmRelSeriesRef.current = null;
      vRelSeriesRef.current = null;
      aRelSeriesRef.current = null;
      topSeriesRef.current = null;
    };
  }, [onTimeRangeChange]);

  // ============== set data ==============
  useEffect(() => {
    if (
      !chartRef.current ||
      !dmmRelSeriesRef.current ||
      !vRelSeriesRef.current ||
      !aRelSeriesRef.current ||
      !topSeriesRef.current
    )
      return;

    if (!data || data.length === 0) {
      dmmRelSeriesRef.current.setData([]);
      vRelSeriesRef.current.setData([]);
      aRelSeriesRef.current.setData([]);
      topSeriesRef.current.setData([]);
      dataRef.current = [];
      setHasData(false);
      return;
    }

    const cleaned = data
      .filter((p) => p?.date)
      .map((p) => ({
        date: String(p.date).slice(0, 10),
        dmmRel: p.dmmRel != null ? Number(p.dmmRel) : null,
        vRel: p.vRel != null ? Number(p.vRel) : null,
        aRel: p.aRel != null ? Number(p.aRel) : null,
        rating: p.rating ?? null,

        topStreak: p.topStreak != null ? Number(p.topStreak) : 0,
        isTop: p.isTop === true,

        preTopStreak: p.preTopStreak != null ? Number(p.preTopStreak) : 0,
        isPreTop: p.isPreTop === true,

        valleyStreak: p.valleyStreak != null ? Number(p.valleyStreak) : 0,
        isValley: p.isValley === true,

        preValleyStreak: p.preValleyStreak != null ? Number(p.preValleyStreak) : 0,
        isPreValley: p.isPreValley === true,
      }))
      .filter(
        (p) =>
          Number.isFinite(p.dmmRel) ||
          Number.isFinite(p.vRel) ||
          Number.isFinite(p.aRel) ||
          (p.topStreak ?? 0) > 0 ||
          (p.preTopStreak ?? 0) > 0 ||
          (p.valleyStreak ?? 0) > 0 ||
          (p.preValleyStreak ?? 0) > 0
      );

    dataRef.current = cleaned;

    if (cleaned.length === 0) {
      dmmRelSeriesRef.current.setData([]);
      vRelSeriesRef.current.setData([]);
      aRelSeriesRef.current.setData([]);
      topSeriesRef.current.setData([]);
      setHasData(false);
      return;
    }

    setHasData(true);

    const dmmRelData = cleaned
      .filter((p) => Number.isFinite(p.dmmRel))
      .map((p) => ({ time: p.date, value: p.dmmRel }));
    const vRelData = cleaned
      .filter((p) => Number.isFinite(p.vRel))
      .map((p) => ({ time: p.date, value: p.vRel }));
    const aRelData = cleaned
      .filter((p) => Number.isFinite(p.aRel))
      .map((p) => ({ time: p.date, value: p.aRel }));

    dmmRelSeriesRef.current.setData(dmmRelData);
    vRelSeriesRef.current.setData(vRelData);
    aRelSeriesRef.current.setData(aRelData);

    // ✅ UMA SÉRIE, valores SEMPRE positivos.
    // Continuidade:
    // - Pré (preTop/preValley): barra translúcida
    // - Confirmado (top/valley): barra sólida
    // Altura segue o streak do estado atual (pre ou confirmado).
    const topData = cleaned.map((p) => {
      const ts = Number(p.topStreak ?? 0);
      const pts = Number(p.preTopStreak ?? 0);
      const vs = Number(p.valleyStreak ?? 0);
      const pvs = Number(p.preValleyStreak ?? 0);

      // prioridade (1 estado por candle): Confirmado > Pré
      // ✅ FIX ESLint no-mixed-operators: parentiza &&/||
      if (
        ts > 0 ||
        (pts > 0 && p.isPreTop) ||
        p.isTop
      ) {
        const value = 3; // ts + pts;
        const confirmed = p.isTop === true; // || ts > 0;
        return {
          time: p.date,
          value,
          color: confirmed ? "rgba(26, 255, 0, 1)" : "rgba(26, 255, 0, 0.35)",
        };
      }

      if (
        vs > 0 ||
        (pvs > 0 && p.isPreValley) ||
        p.isValley
      ) {
        const value = 3; // vs + pvs;
        const confirmed = p.isValley === true; // || vs > 0;
        return {
          time: p.date,
          value,
          color: confirmed ? "rgba(255, 60, 60, 1)" : "rgba(255, 60, 60, 0.35)",
        };
      }

      return { time: p.date, value: 0, color: "rgba(0,0,0,0)" };
    });

    // ✅ range fixo 0..maxAbs (considera topo/pretpo e vale/prevale)
    const maxTop = Math.max(
      1,
      ...cleaned.map((p) => Math.max(Number(p.topStreak ?? 0), Number(p.preTopStreak ?? 0)))
    );
    const maxValley = Math.max(
      1,
      ...cleaned.map((p) => Math.max(Number(p.valleyStreak ?? 0), Number(p.preValleyStreak ?? 0)))
    );
    const maxAbs = Math.max(3, maxTop, maxValley);

    topSeriesRef.current.applyOptions({
      autoscaleInfoProvider: () => ({
        priceRange: { minValue: 0, maxValue: maxAbs },
      }),
    });

    topSeriesRef.current.setData(topData);

    if (!syncedTimeRange) chartRef.current.timeScale().fitContent();
  }, [data, syncedTimeRange]);

  // ============== apply synced range ==============
  useEffect(() => {
    if (!chartRef.current || !syncedTimeRange) return;
    const ts = chartRef.current.timeScale();
    isSyncingRef.current = true;
    ts.setVisibleRange(syncedTimeRange);
    isSyncingRef.current = false;
  }, [syncedTimeRange]);

  const topStateLabel = useMemo(() => {
    if (!hover) return "—";
    if (hover.isTop) return "Topo (Confirmado)";
    if (hover.isPreTop || (hover.preTopStreak ?? 0) > 0) return "Pré-topo (Em formação)";
    if ((hover.topStreak ?? 0) > 0) return "Topo (Em formação)";
    return "—";
  }, [hover]);

  const valleyStateLabel = useMemo(() => {
    if (!hover) return "—";
    if (hover.isValley) return "Vale (Confirmado)";
    if (hover.isPreValley || (hover.preValleyStreak ?? 0) > 0) return "Pré-vale (Em formação)";
    if ((hover.valleyStreak ?? 0) > 0) return "Vale (Em formação)";
    return "—";
  }, [hover]);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* legenda */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 5,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          padding: "6px 10px",
          fontSize: 11,
          background: "rgba(10,10,12,0.75)",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 10,
          color: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(6px)",
          pointerEvents: "none",
        }}
      >
        <LegendDot color="#42a5f5" label="dMM rel" />
        <LegendDot color="#ffeb3b" label="v rel" />
        <LegendDot color="#ef6c00" label="a rel" />
        <LegendDot color="rgba(26, 255, 0, 1)" label="Topo" />
        <LegendDot color="rgba(26, 255, 0, 0.35)" label="Pré-topo" />
        <LegendDot color="rgba(255, 60, 60, 1)" label="Vale" />
        <LegendDot color="rgba(255, 60, 60, 0.35)" label="Pré-vale" />
      </div>

      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {!hasData && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            color: "rgba(255,255,255,0.60)",
            pointerEvents: "none",
            textAlign: "center",
            padding: 12,
          }}
        >
          Sem dados de dinâmica para o período selecionado.
        </div>
      )}

      {hover && hasData && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            padding: "6px 10px",
            fontSize: 11,
            background: "rgba(10,10,12,0.85)",
            color: "rgba(255,255,255,0.9)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 10,
            boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
            pointerEvents: "none",
            lineHeight: 1.4,
            backdropFilter: "blur(6px)",
            zIndex: 6,
            minWidth: 240,
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 4 }}>{hover.date}</div>

          <RowDot color="#42a5f5" label="dMM rel" value={fmt(hover.dmmRel)} />
          <RowDot color="#ffeb3b" label="v rel" value={fmt(hover.vRel)} />
          <RowDot color="#ef6c00" label="a rel" value={fmt(hover.aRel)} />

          <div style={{ opacity: 0.9, marginTop: 4 }}>Rating: {hover.rating ?? "—"}</div>

          <div style={{ opacity: 0.9, marginTop: 2 }}>
            {topStateLabel} — topStreak: {Number(hover.topStreak ?? 0)} — preTopStreak:{" "}
            {Number(hover.preTopStreak ?? 0)}
          </div>

          <div style={{ opacity: 0.9 }}>
            {valleyStateLabel} — valleyStreak: {Number(hover.valleyStreak ?? 0)} — preValleyStreak:{" "}
            {Number(hover.preValleyStreak ?? 0)}
          </div>

          <div style={{ opacity: 0.9 }}>
            Hist(bar): {hover.topHist != null ? String(hover.topHist) : "—"}
          </div>
        </div>
      )}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 999,
          background: color,
          boxShadow: "0 0 0 1px rgba(255,255,255,0.10) inset",
        }}
      />
      <span style={{ opacity: 0.9 }}>{label}</span>
    </span>
  );
}

function RowDot({ color, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: color,
          boxShadow: "0 0 0 1px rgba(255,255,255,0.12) inset",
          flex: "0 0 auto",
        }}
      />
      <div style={{ opacity: 0.92 }}>
        {label}: {value}
      </div>
    </div>
  );
}
